import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, notification, Spin } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import { IUploadFileParams } from "../../../../../../interfaces/files/files";
import {
  ICreateVodPayload,
  IUpdateVodPayload,
} from "../../../../../../interfaces/store/vod-interface";
import {
  createVodStart,
  getVodStart,
  resetVod,
  updateVodStart,
} from "../../../../../../store/features/vod/vod-slice";
import dayjs from "dayjs";
import { blobToBase64 } from "../../../../../../utils/utils";
import {
  AbsoluteButton55,
  RelativeFlex,
  StyledColumnWrapper,
  StyledForm,
  StyledLayout,
} from "../../../../../../components/common/styles";
import {
  ImageInputField,
  TagsDropdownField,
  TextInputField,
} from "../../../../../../components/inputs";
import {
  IMAGE_BASE_URL,
  VIDEO_BASE_URL,
} from "../../../../../../common/config";
import { TimePickerField } from "../../../../../../components/inputs/TimePickerField";
import { useFilterTags } from "../../../../useSelectOptions";

export const TechniqueVideoDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const { vodId } = useParams<{ vodId: string }>();
  const { id: trainerId } = useParams<{ id: string }>();
  const [api, contextHolder] = notification.useNotification();
  const { isEdit } = location.state || {};

  const isNew: boolean = location.pathname.includes("/technique-videos/add");

  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);
  const [videoData, setVideoData] = useState<IUploadFileParams | null>(null);

  const { vod, loading, error, requestState, operation } = useAppSelector(
    (state) => state.vod
  );

  useEffect(() => {
    if (isNew || isEdit) {
      setIsEditable(true);
    }
    if (!isNew) {
      dispatch(getVodStart({ vodId: vodId || "" }));
    }

    return () => {
      dispatch(resetVod());
    };
  }, [dispatch, isNew, vodId]);

  useEffect(() => {
    if (requestState === "finish" && vod?._id) {
      if (operation === "update" || operation === "create") {
        setIsEditable(false);
        navigate(`/trainers/${trainerId}/technique-videos/${vod._id}`, {
          replace: true,
        });
      }
    }
  }, [requestState, operation, vod, navigate, trainerId]);

  useEffect(() => {
    if (vod) {
      form.setFieldsValue({
        name: vod.name,
        tags: vod.tags || [],
        duration: dayjs().startOf("day").second(vod.duration),
      });
    }
  }, [vod]);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  const upsertVideo = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    const duration = dayjs(values.duration);
    const durationInSeconds =
      duration.hour() * 3600 + duration.minute() * 60 + duration.second();

    const videoBody = {
      name: values.name,
      duration: durationInSeconds,
      trainer: trainerId || "",
      tags: values.tags || [],
      // workout: null,
    };

    const imageDataPayload = imageData
      ? {
          ...imageData,
          binaryFile: await blobToBase64(imageData.binaryFile as Blob),
          fileType: "image",
        }
      : null;

    const videoDataPayload = videoData
      ? {
          ...videoData,
          binaryFile: await blobToBase64(videoData.binaryFile as Blob),
          fileType: "video",
        }
      : null;

    const upsertVideoBody = {
      vod: { ...videoBody },
      ...(isNew ? {} : { vodId: vod?._id }),
      ...(imageDataPayload && { imageData: imageDataPayload }),
      ...(videoDataPayload && { videoData: videoDataPayload }),
    };

    if (!videoData?.binaryFile) {
      openNotification("Please note that no video was provided", true);
    }

    dispatch(
      isNew
        ? createVodStart(upsertVideoBody as ICreateVodPayload)
        : updateVodStart(upsertVideoBody as IUpdateVodPayload)
    );
  };

  const openNotification = (message?: string, isWarning?: boolean) => {
    api[isWarning ? "warning" : "error"]({
      message: t(message || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const filterTags = useFilterTags();

  return (
    <>
      {contextHolder}
      <RelativeFlex>
        <AbsoluteButton55 onClick={() => form.submit()}>
          {isEditable ? t("Save") : t("Edit")}
        </AbsoluteButton55>
      </RelativeFlex>

      <Spin spinning={loading}>
        <StyledForm
          form={form}
          onFinish={upsertVideo}
          validateTrigger="onSubmit"
        >
          <StyledLayout>
            <StyledColumnWrapper>
              <TextInputField
                name="name"
                label={t("Video Name")}
                isEditable={isEditable}
                rules={[{ required: true, message: t("Required") }]}
              />
              <ImageInputField
                label={t("Video")}
                isEditable={isEditable}
                value={
                  vod?.video ? VIDEO_BASE_URL + "/" + vod.video : undefined
                }
                onChange={setVideoData}
                type="video"
                isLargeVideoStyle={true}
                aspect={1}
              />
              <TimePickerField
                name="duration"
                label={t("Video Duration")}
                isEditable={isEditable}
                rules={[{ required: true, message: t("Required") }]}
              />
              <TagsDropdownField
                mode="multiple"
                label={t("Tags")}
                options={filterTags}
                name="tags"
                isEditable={isEditable}
              />
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Video Cover Image")}
                isEditable={isEditable}
                value={
                  vod?.image ? IMAGE_BASE_URL + "/" + vod.image : undefined
                }
                onChange={setImageData}
                aspect={147 / 187}
              />
            </StyledColumnWrapper>
          </StyledLayout>
        </StyledForm>
      </Spin>
    </>
  );
};
